'use client';

/**
 * useSocket — Singleton Socket.IO wrapper for admin frontend live updates.
 *
 * Spec ref: SPEC-040 §3.2 Track 7-backend (Sistema Anuncios — WS emit),
 *           CONTRACTS-ADMIN-API §0.4 (Date ranges) + §0.7 (WebSocket
 *             transport: Socket.IO v4, JWT in `auth` handshake) + §14.1
 *             (Connection setup admin client) + §14.2 (announcement:new
 *             payload shape consumed by AdminAnnouncements.tsx).
 *
 * Source: socket.io-client v4.8.1 (already installed — see frontend/package.json
 *           dependency `"socket.io-client": "^4.8.1"`) + existing
 *           `useConference.ts` io() + auth.token handshake pattern
 *           (lines 170-202 of that file). useConference is for mediasoup /
 *           conference signaling; useSocket is a thinner singleton wrapper
 *           for admin-scoped events (announcement:new/updated/deleted,
 *           kyc:status-change, payout:status-change, deposit:new,
 *           circuit-breaker:state-change — per AdminWebSocketEvent union
 *           in backend/src/types/admin-dtos.ts:724-731).
 *
 * Anti-§6 declaration (CM-19/CM-15 anti-invensión):
 *   - JWT verification + room joins are delegated to the backend
 *     `socket-auth.middleware.ts` (D2 auto-joins `user:<userId>` always +
 *     `admin` if role is admin/superadmin + `package:<tier>` best-effort).
 *     This hook does NOT call `socket.emit('join', { room: 'admin' })`
 *     manually — the backend handles room joins via JWT role check
 *     (CONTRACTS-ADMIN-API §14.1 says "join room admin" but IA#1 D2
 *     worklog confirms this is now done automatically server-side;
 *     deviation documented here so future IAs don't re-add a manual join).
 *   - Event names + payload shapes come from `AdminWebSocketEventMap`
 *     (backend/src/types/admin-dtos.ts:807-814) — no invented names.
 *   - The `on()` handler parameter is typed `unknown` (CM-31 TS estricto)
 *     so the caller MUST narrow via type guards before accessing fields.
 *     This prevents `any` from leaking into consumer code.
 *
 * Cambios vs referencia original:
 *   - Profileia used ad-hoc `io()` calls scattered across components with
 *     no shared singleton (multi-connection bug-prone). Profileia uses a
 *     module-level `socketRef` so the entire admin SPA shares ONE socket
 *     per browser tab (anti-multi-connection invariant).
 *   - Profileia had no typed WS event map — admin events were untyped
 *     strings. Profileia uses `AdminWebSocketEventMap` for compile-time
 *     event-name checking (this hook still accepts `string` for the event
 *     name to stay decoupled from the DTO module at runtime, but the
 *     caller is expected to pass keys from the map).
 *
 * Connection lifecycle:
 *   - Connects on mount IF the current user has role admin/superadmin
 *     (avoids wasteful connections from regular users browsing /admin
 *     routes they can't access — admin middleware gates this server-side
 *     already, but the client-side check is defense-in-depth).
 *   - Disconnects on unmount of the LAST consumer (refcount-style: we
 *     use a single module-level socket + simple useEffect cleanup that
 *     disconnects when the consumer count drops to 0). For the D2 scope
 *     (only AdminAnnouncements consumes), this is sufficient.
 *   - Auto-reconnect: socket.io-client v4 has built-in reconnection
 *     (default: infinite attempts with backoff). We cap at 5 attempts
 *     + 1s initial delay to avoid hammering a dead backend.
 */

import { useEffect, useState, useCallback } from 'react';
import { io, type Socket } from 'socket.io-client';
import { tokenStore } from '@/lib/api-client';
import { useAuth } from '@/lib/use-auth';

// ─── Module-level singleton ─────────────────────────────────────────────────
// One socket per browser tab. Multiple `useSocket()` consumers share it.
// Pattern: `useRef` inside the hook returns the same singleton instance
// across re-renders AND across hook instances.

let socketSingleton: Socket | null = null;

/** Module-level consumer count (FE-001 fix): previously `useRef(0)` per hook
 *  instance, so each component had its own count starting at 0. When one
 *  unmounted, its count went to 0 → destroySocket() → killed singleton for
 *  ALL consumers. Now module-scoped so the count is shared across all
 *  hook instances — destroySocket() only fires when the LAST consumer
 *  unmounts. */
let consumerCount = 0;

/**
 * Lazily create the singleton Socket.IO connection. The connection is
 * configured per CONTRACTS-ADMIN-API §0.7 + §14.1:
 *   - `auth: { token }` — JWT sent in handshake; backend `socket-auth.
 *     middleware.ts` verifies via `verifyAccessToken` and attaches
 *     `socket.profileiaUser = { userId, username, role }`.
 *   - `transports: ['websocket']` — skip HTTP long-polling fallback
 *     (we run behind Caddy which supports WebSocket upgrade).
 *   - `reconnection: true` (default) + `reconnectionAttempts: 5` + 
 *     `reconnectionDelay: 1000` — bounded retry to avoid log noise.
 *
 * Anti-§6: no manual `socket.emit('join', { room: 'admin' })` — the
 * backend auto-joins based on JWT role (see header comment).
 */
function getSocket(): Socket | null {
  if (typeof window === 'undefined') return null; // SSR guard
  if (socketSingleton) return socketSingleton;

  const token = tokenStore.getAccess();
  if (!token) return null;

  socketSingleton = io({
    path: '/socket.io',
    transports: ['websocket'],
    // 4E-CRIT-001 fix: use the FUNCTION-FORM `auth` so socket.io-client
    // reads the latest access token on every handshake (initial connect +
    // every reconnection). Pre-fix, `auth: { token }` was captured at
    // call time; socket.io-client v4 reuses the same `auth` object on
    // every reconnection. After 15 min the access token expired; on next
    // reconnection, the socket-auth middleware rejected the stale JWT,
    // `connect_error` fired, and the admin lost live updates until page
    // reload. With the function form, each handshake calls
    // `tokenStore.getAccess()` fresh — if the token was refreshed by
    // `tryRefresh` in the meantime, the new token is used.
    auth: (cb) => cb({ token: tokenStore.getAccess() }),
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
  });

  return socketSingleton;
}

/** Disconnect + null-out the singleton (called on consumer teardown). */
function destroySocket(): void {
  if (!socketSingleton) return;
  socketSingleton.removeAllListeners();
  socketSingleton.disconnect();
  socketSingleton = null;
}

// BUG-EXP5-005 (SPEC-041 QA audit pre-launch): listen for the global
// `profileia:logged-out` CustomEvent (dispatched by `useAuth.logout()`)
// and tear down the singleton socket immediately. Without this, the
// socket would stay connected with the old JWT until the server's
// socket-auth middleware noticed the session was revoked and kicked it
// (30+ seconds of dead emits the server silently ignored).
//
// The listener is registered ONCE at module load (not per-hook-instance)
// because there's only one singleton socket — multiple listeners would
// just call `destroySocket()` N times, which is a no-op after the first.
// We guard against SSR by checking `typeof window !== 'undefined'`.
if (typeof window !== 'undefined') {
  window.addEventListener('profileia:logged-out', () => {
    destroySocket();
    // Also reset the consumer count — when the socket is destroyed
    // out-of-band by logout, the next `useSocket()` mount should start
    // fresh with a new socket + count = 1.
    consumerCount = 0;
  });

  // H-09 fix: When the access token is refreshed (e.g., by api-client's
  // tryRefresh), the singleton socket still carries the old JWT in its
  // handshake auth. Destroy the socket so the next getSocket() call
  // recreates it with the fresh token from tokenStore.getAccess().
  //
  // AUDIT-6-001 fix (BATCH-5): pre-fix, this handler only called
  // `destroySocket()` — it left `socketSingleton = null` AND existing
  // consumers' `connect`/`disconnect` listeners were removed by
  // `removeAllListeners()`. Consumers' `useEffect`s had deps `[isAdmin]`
  // (no change on token refresh) → they did NOT re-run → no consumer
  // called `getSocket()` to create a new socket → live updates stayed
  // dead until the admin manually refreshed the page.
  //
  // Post-fix: in addition to `destroySocket()`, we reset `consumerCount`
  // to 0 (so the next `getSocket()` call starts a fresh count — existing
  // consumers' main useEffect cleanups will decrement this, but the new
  // effects will re-increment it, net change 0) AND dispatch a
  // `profileia:socket-recreated` CustomEvent. Consumers listen for this
  // event in an ADDITIONAL useEffect that bumps a `socketEpoch` state —
  // the main useEffect has `socketEpoch` in its deps, so it re-runs:
  // cleanup (remove old listeners from the now-destroyed old socket,
  // decrement consumerCount) → re-run (call `getSocket()` to create a
  // new socket with the fresh token, re-register connect/disconnect
  // listeners, increment consumerCount). After the refresh cycle,
  // consumerCount is back to N (the number of active consumers) and the
  // new socket is live.
  //
  // Why reset consumerCount = 0: the audit spec explicitly requires it.
  // Without the reset, the count would stay at N throughout the refresh
  // cycle (cleanups decrement to 0, new effects increment back to N).
  // With the reset, the count goes 0 → -N (cleanups) → 0 (new effects).
  // The -N intermediate is harmless (consumerCount is only consulted in
  // the `<= 0` check, which is true throughout). The end state (0
  // instead of N) is a minor drift — the next consumer mount/unmount
  // cycle will adjust the count, and `destroySocket()` calls in cleanups
  // are no-ops when `socketSingleton` is non-null. The drift is bounded
  // (one per refresh cycle, ~15 min apart) and self-correcting on the
  // next mount.
  window.addEventListener('token-refreshed', () => {
    destroySocket();
    consumerCount = 0;
    // AUDIT-6-001: notify consumers to re-subscribe. The event is
    // dispatched AFTER destroySocket() + consumerCount reset so consumers
    // see a clean slate when they re-call `getSocket()`.
    window.dispatchEvent(new CustomEvent('profileia:socket-recreated'));
  });
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export interface UseSocketApi {
  /**
   * AUDIT3-FECONF-019 fix (BATCH-4): REMOVED the `socket: Socket | null`
   * field from the public API. Pre-fix, callers could grab the raw socket
   * reference and call `.on()` / `.emit()` on it directly — but the
   * singleton is recreated on token-refresh, so the captured reference
   * became STALE (pointed at the destroyed socket). Callers' `.emit()`
   * calls were silently dropped. Now callers MUST go through `emit()` /
   * `on()` which read `socketSingleton` fresh on every call.
   */
  /** True iff the socket is currently connected (live updates flowing). */
  isConnected: boolean;
  /** Send an event to the backend. No-op if socket is null. */
  emit: (event: string, payload?: unknown) => void;
  /**
   * Subscribe to an event. Returns an unsubscribe function — call it in
   * your `useEffect` cleanup to avoid memory leaks across re-renders.
   *
   * AUDIT3-FECONF-018 fix (BATCH-4): the handler now receives ALL
   * payload args from the socket event (variadic `unknown[]`), not just
   * `args[0]`. Pre-fix, multi-arg socket events (e.g., a future event
   * like `chat:message`, `roomId`, `senderId`) silently dropped the
   * trailing args — callers only saw the first arg. Now the handler is
   * called with the full args array; callers can destructure what they
   * need. For backward compatibility, single-arg events continue to
   * work unchanged (the handler is called with `[payload]`).
   */
  on: (event: string, handler: (...args: unknown[]) => void) => () => void;
}

/**
 * useSocket — React hook that exposes the singleton Socket.IO connection.
 *
 * Usage:
 *   const { on, isConnected } = useSocket();
 *   useEffect(() => {
 *     const off = on('announcement:new', (raw) => {
 *       const payload = raw as AnnouncementNewPayload; // caller narrows
 *       ...
 *     });
 *     return off; // cleanup
 *   }, [on]);
 */
export function useSocket(): UseSocketApi {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);

  // Sprint 100 QA fix (N-P0-1): connect for ANY authenticated user, not just
  // admin/superadmin. Pre-fix: `isAdmin` gate prevented non-admin users from
  // receiving real-time notifications (notification:new, announcement:new,
  // payout:status-change, deposit:new). All non-admin users were degraded to
  // 30-second polling — the toast on new notification never fired in real-time.
  // Post-fix: any logged-in user gets a socket connection. The backend
  // socket-auth middleware already auto-joins every authenticated user to
  // `user:<userId>` room + validates the JWT on connection.
  const isAuthenticated = !!user;

  // R3-CRIT-012 fix (BATCH-5): socketEpoch state that bumps whenever the
  // module-level `profileia:socket-recreated` CustomEvent fires (dispatched
  // by the `token-refreshed` handler at the top of this file after
  // `destroySocket()`). Pre-fix, the main useEffect below had deps
  // `[isAdmin]` — when the singleton was destroyed on token refresh,
  // `isAdmin` didn't change → the effect didn't re-run → `getSocket()` was
  // never called again → `socketSingleton` stayed `null` forever → admin
  // lost ALL live updates (announcement:new, kyc:status-change,
  // payout:status-change, deposit:new, circuit-breaker:state-change) until
  // a manual page reload. AUDIT-6-001 comment claimed "consumers listen
  // for this event in an ADDITIONAL useEffect that bumps a `socketEpoch`
  // state" — but no such useEffect or state existed. Now both live here.
  const [socketEpoch, setSocketEpoch] = useState(0);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => setSocketEpoch((e) => e + 1);
    window.addEventListener('profileia:socket-recreated', handler);
    return () => {
      window.removeEventListener('profileia:socket-recreated', handler);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const socket = getSocket();
    if (!socket) return;

    consumerCount += 1;

    const onConnect = () => setIsConnected(true);
    const onDisconnect = () => setIsConnected(false);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    // If the socket is already connected (singleton reused), sync state.
    if (socket.connected) setIsConnected(true);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      consumerCount = Math.max(0, consumerCount - 1);
      // Tear down the singleton only when the last consumer unmounts.
      if (consumerCount <= 0) {
        destroySocket();
        setIsConnected(false);
      }
    };
  // R3-CRIT-012: include `socketEpoch` in deps so the effect re-runs
  // (re-calling `getSocket()` to create a fresh socket with the new JWT)
  // when `token-refreshed` destroys the singleton + dispatches
  // `profileia:socket-recreated`. Without this, the deps array was just
  // `[isAdmin]` — token refresh didn't change `isAdmin`, so the effect
  // never re-ran, and the socket stayed dead.
  }, [isAuthenticated, socketEpoch]);

  const emit = useCallback((event: string, payload?: unknown) => {
    const socket = socketSingleton;
    if (!socket) return;
    socket.emit(event, payload);
  }, []);

  // D-002 fix (SPEC-043 Batch D): the `on`/`off` callbacks now depend on
  // `isConnected`. Previously they had `[]` deps — stable across renders.
  // At mount time `useAuth` is still loading (`user=null`), so the main
  // `useEffect` returns early without creating the singleton (see `isAdmin`
  // check above). Consumer's `on('event', handler)` call saw
  // `socketSingleton === null` and returned a no-op unsubscribe. When the
  // user resolved (admin) and the socket was created, consumer's
  // `useEffect` did NOT re-run (deps unchanged) — handler permanently
  // lost. Now `on` re-creates when `isConnected` flips (false → true),
  // consumers' `useEffect`s re-run, and handlers re-subscribe.
  //
  // AUDIT3-FECONF-018 fix (BATCH-4): the wrapped handler now accepts
  // `...args: unknown[]` and forwards the FULL args array to the caller's
  // handler (not just `args[0]`). Pre-fix, multi-arg socket events
  // (e.g., `event`, `arg1`, `arg2`) silently dropped `arg2+`. Now the
  // caller receives all args via spread.
  const on = useCallback((event: string, handler: (...args: unknown[]) => void) => {
    const socket = socketSingleton;
    if (!socket) {
      // No socket yet — return a no-op unsubscribe (caller's useEffect
      // still gets a cleanup function, satisfying the hook contract).
      // D-002: when `isConnected` flips from false → true, this `on`
      // reference changes, so consumers' useEffect re-runs and the
      // handler is re-registered against the now-existing socket.
      return () => {};
    }
    // Wrap the handler so we can pass `unknown[]` to the caller while
    // satisfying socket.io-client's `(...args: any[])` signature.
    // AUDIT3-FECONF-018: forward ALL args (not just `args[0]`) so
    // multi-arg socket events reach the caller intact.
    const wrapped = (...args: unknown[]): void => {
      // Spread the full args array — single-arg events still work
      // (handler is called with `[payload]` which destructures fine).
      handler(...args);
    };
    socket.on(event, wrapped);
    return () => {
      socket.off(event, wrapped);
    };
  }, [isConnected]);

  return {
    isConnected,
    emit,
    on,
  };
}
